/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import React, { Fragment } from 'react';
import isEmpty from 'lodash/isEmpty';
import { createLogic } from 'redux-logic';
import { clearCMM } from 'state/contactMethodMaintenance/ContactMethodMaintenanceActions';
import {
  contactInfoWasSaved,
  clearTouchedFields
} from 'state/validation/ValidationActions';
import {
  disableContactInfoSave,
  hideFixedFooterSaveOptions
} from 'state/preferences/PreferencesActions';
import {
  dispatchBusinessEvent,
  RECEIVED_UPDATE_CUSTOMER_PREFERENCES
} from 'state/preferences/PreferencesActions';
import { addToast } from 'state/toast/ToastActions';
import {
  verifyContactInformation,
  verifyContactInformationV2
} from 'services/Rest';
import { residentialAddressEnabled } from 'services/FeatureFlags';
import { getCustomerContactInformation } from 'state/customer/CustomerSelectors';
import Store from 'state/Store';
import {
  analyticsActionError,
  analyticsActionSuccess
} from 'state/analytics/AnalyticsActions';
import { footerLinks } from 'services/Externals';
import { NON_BREAKING_SPACE } from 'services/Formats';

const UpdatePreferencesLogic = createLogic({
  type: RECEIVED_UPDATE_CUSTOMER_PREFERENCES,

  process({ action }, dispatch, done) {
    const { preferences } = action;
    const {
      contactInformation,
      accountVisibility,
      costBasisElections,
      beneficiaries,
      trustedContacts,
      defaultGroupBy,
      customAccountGroups,
    } = preferences;

    if (contactInformation) {
      const { phoneNumbers, primaryEmailAddress } =
        contactInformation;
      const errors = [];
      const phoneNumberError = phoneNumbers
        .filter((phone) => !isEmpty(phone))
        .some((phoneNumber) => {
          const { disposition } = phoneNumber;
          return disposition && disposition.result === false;
        });

      if (phoneNumberError) {
        errors.push('phone number');
      }

      const emailDisposition =
        primaryEmailAddress && primaryEmailAddress.disposition;

      if (emailDisposition && emailDisposition.result === false) {
        errors.push('email address');
      }

      const mailingDisposition = contactInformation.primaryMailingAddress &&
        contactInformation.primaryMailingAddress.disposition;

      if (mailingDisposition && mailingDisposition.result === false) {
        errors.push('mailing address');
      }

      let residentialDisposition;
      if (residentialAddressEnabled()) {
        residentialDisposition = contactInformation.residentialAddress &&
          contactInformation.residentialAddress.disposition;
      }

      if (residentialDisposition && residentialDisposition.result === false) {
        errors.push('residential address')
      }

      if (errors.length === 0) {
        // dispatch user message
        dispatch(addToast(true, 'Changes Saved'));

        // dispatch analytics message
        dispatch(
          analyticsActionSuccess('ProfPref', 'UpdateContactInformation')
        );

        if (residentialAddressEnabled()) {
          verifyContactInformationV2(
            contactInformation.primaryMailingAddress,
            primaryEmailAddress,    
            phoneNumbers,
            contactInformation.residentialAddress,
          );
        } else {
          verifyContactInformation(
            contactInformation.primaryMailingAddress,
            primaryEmailAddress,
            phoneNumbers
          );
        }

        // clear the contact method maintenance
        dispatch(clearCMM());
        //let the update contact info component know the save was successful to show disclosure
        dispatch(contactInfoWasSaved(true));
        //disable save button after successful response
        dispatch(disableContactInfoSave());
        // hide save options after successful response
        dispatch(hideFixedFooterSaveOptions());
        dispatch(clearTouchedFields());

        // prep to dispatch one or more business events
        const { previousEmailAddress } = getCustomerContactInformation(
          Store.getState()
        );
        const businessEvent = {
          ...contactInformation,
          previousEmailAddress
        };
        dispatch(dispatchBusinessEvent(businessEvent));
      } else {
        // dispatch user message
        const title = '';
        const message = `There was an error updating the following information: ${errors.join(
          ', '
        )}.`;
        dispatch(addToast(false, title, message));

        // dispatch analytics message
        dispatch(analyticsActionError('UpdateContactInformation'));

        //disable save button after failed response
        dispatch(disableContactInfoSave());
        dispatch(clearTouchedFields());
      }
    }

    if (!isEmpty(trustedContacts)) {
      const trustedContactsError = trustedContacts.some((contact) => {
        const { disposition } = contact;
        return disposition && disposition.result === false;
      });

      if (!trustedContactsError) {
        // dispatch user message
        dispatch(addToast(true, 'Changes Saved'));

        // dispatch analytics message
        dispatch(analyticsActionSuccess('ProfPref', 'UpdateTrustedContacts'));
      } else {
        // dispatch user message
        const title = '';
        const message = () => {
          return (
            <Fragment>
              Unfortunately, we were unable to save the changes to your trusted
              contacts due to an error in our processes. Please
              {NON_BREAKING_SPACE}
              <a
                href={footerLinks.contactUsUrl}
                target="_blank"
                rel="noreferrer">
                contact us
              </a>
              {NON_BREAKING_SPACE}
              for assistance or try again later.
            </Fragment>
          );
        };
        dispatch(addToast(false, title, message));

        // dispatch analytics message
        dispatch(analyticsActionError('UpdateTrustedContacts'));
      }
    }

    if (beneficiaries) {
      const beneficiariesError = beneficiaries.some((beneficiary) => {
        const { disposition } = beneficiary;
        return disposition && disposition.result === false;
      });

      if (!beneficiariesError) {
        dispatch(addToast(true, 'Changes Saved'));

        const businessEvent = {
          beneficiaries
        };
        dispatch(dispatchBusinessEvent(businessEvent));
      } else {
        const title = '';
        const message = () => {
          return (
            <Fragment>
              Unfortunately, we were unable to save the changes to your
              beneficiary information due to an error in our processes. Please
              try again later or{NON_BREAKING_SPACE}
              <a
                href={footerLinks.contactUsUrl}
                target="_blank"
                rel="noreferrer">
                contact us
              </a>
              {NON_BREAKING_SPACE}
              for an alternate way to name or change your beneficiaries.
            </Fragment>
          );
        };
        dispatch(addToast(false, title, message));
      }
    }

    if (customAccountGroups) {
      const customAccountGroupsError = customAccountGroups.some(
        (accountGroup) => {
          const { disposition } = accountGroup;
          return disposition && disposition.result === false;
        }
      );
      if (!customAccountGroupsError) {
        // dispatch user message
        dispatch(addToast(true, 'Changes Saved'));

        // dispatch analytics message
        dispatch(
          analyticsActionSuccess('ProfPref', 'UpdateCustomizeAccountView')
        );
      } else {
        // dispatch user message
        const title = '';
        const message =
          'Unfortunately, we were unable to save the changes to your custom account groupings due to an error in our processes. Please try again later.';
        dispatch(addToast(false, title, message));

        // dispatch analytics message
        dispatch(analyticsActionError('UpdateCustomAccountGroup'));
      }
    }

    if (costBasisElections) {
      const costBasisElectionsError = costBasisElections.some((costBasis) => {
        const { disposition, brokerage, brokerageCostBasisMethods } = costBasis;
        const hasBrokerageError = brokerage &&
          preferences.brokerageTransaction &&
          (brokerageCostBasisMethods?.recordKeeperStatus.brokerageRequestStatus === 'FAILURE' ||
          brokerageCostBasisMethods?.recordKeeperStatus.brokerageRequestStatus === 'UNKNOWN')
        return hasBrokerageError || (disposition && disposition.result === false);
      });

      if (!costBasisElectionsError) {
        // dispatch user message
        dispatch(addToast(true, 'Cost basis updated'));

        // dispatch analytics message
        dispatch(analyticsActionSuccess('ProfPref', 'UpdateCostBasisMethod'));
      } else {
        // dispatch user message
        const title = '';
        const message = 'An error occurred when updating your cost basis';
        dispatch(addToast(false, title, message));

        // dispatch analytics message
        dispatch(analyticsActionError('UpdateCostBasisMethod'));
      }
    }
    if (accountVisibility) {
      // dispatch user message
      dispatch(addToast(true, 'Changes Saved'));

      // dispatch analytics message
      dispatch(analyticsActionSuccess('ProfPref', 'UpdateAccountVisibility'));
    }

    if (defaultGroupBy) {
      const { disposition } = defaultGroupBy;
      const defaultGroupByError = disposition && disposition.result === false;

      if (!defaultGroupByError) {
        // dispatch user message
        dispatch(addToast(true, 'Changes Saved'));

        // dispatch analytics message
        dispatch(
          analyticsActionSuccess('ProfPref', 'UpdateAccountViewSettings')
        );
      } else {
        // dispatch user message
        const title = '';
        const message =
          'Unfortunately, we were unable to save the changes to your account view settings due to an error in our processes. Please try again later.';
        dispatch(addToast(false, title, message));

        // dispatch analytics message
        dispatch(analyticsActionError('UpdateAccountViewSettings'));
      }
    }

    done();
  }
});

export default UpdatePreferencesLogic;
